import { useEffect } from "react";
import { Track, type LocalTrack, type Room } from "livekit-client";
import { armProbe, initialStall, nextStall, PROBE_MS, receiptFrom } from "../lib/mediaStall";
import { emitCallActivity } from "../lib/callActivity";

const SAMPLE_MS = 1000;

/**
 * Replaces a call connection that looks alive but no longer gets our audio
 * to the server (#2223 AC13, AC24–AC26).
 *
 * Samples once a second (see `lib/mediaStall.ts` for what counts as
 * stalled), and at once when the network changes (`navigator.connection`
 * change where supported, or `online`): then a new server receipt must
 * arrive within PROBE_MS. The publisher's ICE state would be a third
 * trigger, but it is only reachable through LiveKit internals, so it is not
 * used.
 *
 * On a stall it disconnects the room: both rejoin loops (signed-in and
 * guest) treat a client-side disconnect as involuntary and bring the person
 * back with a fresh connection. After it the room is disconnected and the
 * stall state resets until the next connection.
 */
export function useMediaStallRecovery(room: Room): void {
  useEffect(() => {
    let state = initialStall();
    let stopped = false;
    let inflight: Promise<void> | null = null;

    const run = async () => {
      const track = room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track as
        | LocalTrack
        | undefined;
      const mst = track?.mediaStreamTrack;
      const micLive = !!mst && mst.readyState === "live" && !mst.muted;
      const report = micLive ? await track!.getRTCStatsReport().catch(() => undefined) : undefined;
      if (stopped) return;
      const r = nextStall(state, { state: room.state, micLive, receipt: receiptFrom(report) }, Date.now());
      state = r.state;
      if (r.stalled) {
        // For the call recorder (#2223 J1) only; the decision is already made.
        const trigger =
          r.state.reconnectingSince !== null ? "reconnecting_cap" : r.state.probeAt !== null ? "network_probe" : "receipt_stall";
        emitCallActivity("recovery", { trigger, step: "disconnect", result: "started" });
        state = initialStall();
        void room.disconnect().catch(() => {});
      }
    };
    const sample = (): Promise<void> => {
      if (stopped) return Promise.resolve();
      inflight ??= run().finally(() => {
        inflight = null;
      });
      return inflight;
    };

    let probeTimer: ReturnType<typeof setTimeout> | null = null;
    const onNetworkChange = async () => {
      // A sample already under way may predate the change; take a fresh one.
      if (inflight) await inflight;
      await sample();
      if (stopped) return;
      state = armProbe(state, Date.now());
      if (state.probeAt === null) return;
      if (probeTimer) clearTimeout(probeTimer);
      probeTimer = setTimeout(() => void sample(), PROBE_MS);
    };
    const onChange = () => void onNetworkChange();

    const connection = (navigator as Navigator & { connection?: EventTarget }).connection;
    connection?.addEventListener?.("change", onChange);
    window.addEventListener("online", onChange);
    const id = setInterval(() => void sample(), SAMPLE_MS);
    return () => {
      stopped = true;
      clearInterval(id);
      if (probeTimer) clearTimeout(probeTimer);
      connection?.removeEventListener?.("change", onChange);
      window.removeEventListener("online", onChange);
    };
  }, [room]);
}
